/**
 * The scoring vocabulary, fetched once and shared.
 *
 * Weights are NOT duplicated in the frontend. They come from the same
 * constants the pipeline scored with, so a breakdown shown on hover can never
 * disagree with the number beside it.
 *
 * **Asked again, once, for a flag it has never heard of.** The server takes a
 * new engine catalog when it copies a county, so a page can hold the rules it
 * loaded before a release and then load rows carrying the release's new
 * signal: on 29 Sep 2026 every Sarasota row showed *tax delinquent — Reload
 * for the current weights* under a total the popover did not reach. A flag
 * the rules do not name is that and nothing else, so the rules are fetched
 * again and every table holding them re-renders. Once per flag: a flag the
 * server does not know either costs one request, not one per render.
 */

import { useEffect, useState } from "react";

import { api } from "../api/client";
import type { ScoringRules } from "../api/types";

let cached: ScoringRules | null = null;
let inFlight: Promise<ScoringRules> | null = null;
const listeners = new Set<(rules: ScoringRules) => void>();
const askedFor = new Set<string>();

function load(): Promise<ScoringRules> {
  // One request for the whole app, however many tables mount.
  inFlight ??= api.scoring().then((response) => {
    cached = response;
    listeners.forEach((listener) => listener(response));
    return response;
  });
  inFlight.catch(() => {
    inFlight = null; // let a later mount retry
  });
  return inFlight;
}

/** The flags on these rows that the rules in hand do not name. */
export function unknownFlags(rules: ScoringRules | null,
                             flags: readonly string[]): string[] {
  if (!rules) return [];
  const known = new Set(rules.signals.map((signal) => signal.flag));
  return [...new Set(flags)].filter((flag) => !known.has(flag));
}

/**
 * `flags` are the signals on the rows the caller is about to explain; any the
 * rules do not name sends for the rules again.
 */
export function useScoring(flags: readonly string[] = []): ScoringRules | null {
  const [rules, setRules] = useState<ScoringRules | null>(cached);

  useEffect(() => {
    listeners.add(setRules);
    if (cached) setRules(cached);
    else load().catch(() => undefined);
    return () => {
      listeners.delete(setRules);
    };
  }, []);

  const key = unknownFlags(rules, flags).filter((flag) => !askedFor.has(flag))
    .join("|");
  useEffect(() => {
    if (key) askAgainFor(key.split("|"));
  }, [key]);

  return rules;
}

/**
 * Fetch the rules again if they do not name one of these flags and it has not
 * been asked about yet. True when a request was sent.
 */
export function askAgainFor(flags: readonly string[]): boolean {
  const missing = unknownFlags(cached, flags).filter((flag) => !askedFor.has(flag));
  if (!cached || !missing.length) return false;
  for (const flag of missing) askedFor.add(flag);
  inFlight = null;
  load().catch(() => undefined);
  return true;
}

/** Load the rules outside a component, for tests. */
export function loadScoring(): Promise<ScoringRules> {
  return load();
}

/** For tests: forget the rules, the requests and the flags asked about. */
export function resetScoringCache(): void {
  cached = null;
  inFlight = null;
  listeners.clear();
  askedFor.clear();
}
